import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TourProvider } from './contexts/TourContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import FracasList from './pages/FracasList';
import FracasCreate from './pages/FracasCreate';
import FracasDetail from './pages/FracasDetail';
import FiveWhyTool from './pages/FiveWhyTool';
import IshikawaTool from './pages/IshikawaTool';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import RCAToolsHub from './pages/RCAToolsHub';
import EightDList from './pages/EightDList';
import EightDWorksheet from './pages/EightDWorksheet';
import AdvancedAnalyticsHub from './pages/AdvancedAnalyticsHub';
import WeibullTool from './pages/WeibullTool';
import MTBFCalculator from './pages/MTBFCalculator';
import WarrantyTracking from './pages/WarrantyTracking';
import FTAEditor from './pages/FTAEditorPage';
import ExecutiveCockpit from './pages/ExecutiveCockpit';
import AdvancedReports from './pages/AdvancedReports';
import LabSampleList from './pages/LabSampleList';
import LabSampleDetail from './pages/LabSampleDetail';
import AIAssistantPage from './pages/AIAssistantPage';
import UserGuidePage from './pages/UserGuidePage';
import CustomerPortal from './pages/CustomerPortal';
import SupplierPortal from './pages/SupplierPortal';
import MethodologyTemplates from './pages/MethodologyTemplates';
import RBDTool from './pages/RBDTool';
import { LabSampleProvider } from './contexts/LabSampleContext';
import { FracasProvider } from './contexts/FracasContext';
import { EightDProvider } from './contexts/EightDContext';
import { LearnProvider } from './contexts/LearnContext';

// Protected Route Wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
};

// Main App Component
const App: React.FC = () => {
    const { user, loading, logout } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading Magnus Resolve...</p>
                </div>
            </div>
        );
    }

    // ISO 27001 CRITICAL SECURITY CHECK
    // Block unapproved users BEFORE showing any app content
    const isApproved = user?.isApproved === true && user?.role !== 'pending';
    const isAutoGainAdmin = user?.role === 'autoGainAdmin';

    if (user && !isApproved && !isAutoGainAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
                <div className="text-center max-w-md p-8 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                        <svg className="w-10 h-10 text-amber-600 dark:text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Access Pending</h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                        Your account is awaiting administrator approval. You will be notified once your access has been granted.
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
                        Email: {user.email}
                    </p>
                    <button
                        onClick={() => logout()}
                        className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                        Sign Out
                    </button>
                </div>
            </div>
        );
    }

    return (
        <ThemeProvider>
            <TourProvider>
                <LearnProvider>
                    <FracasProvider>
                        <EightDProvider>
                            <LabSampleProvider>
                                <Routes>
                                    <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" replace />} />

                                    <Route path="/" element={
                                        <ProtectedRoute>
                                            <Layout />
                                        </ProtectedRoute>
                                    }>
                                        <Route index element={<Dashboard />} />
                                        <Route path="/customer-portal" element={<CustomerPortal />} />
                                        <Route path="/supplier-portal" element={<SupplierPortal />} />
                                        <Route path="/fracas" element={<FracasList />} />
                                        <Route path="/fracas/create" element={<FracasCreate />} />
                                        <Route path="/fracas/:id" element={<FracasDetail />} />
                                        <Route path="/8d" element={<EightDList />} />
                                        <Route path="/8d/create" element={<EightDWorksheet />} />
                                        <Route path="/8d/:id" element={<EightDWorksheet />} />
                                        <Route path="/rca" element={<RCAToolsHub />} />
                                        <Route path="/rca-hub" element={<Navigate to="/rca" replace />} />
                                        <Route path="/rca/5why" element={<FiveWhyTool />} />
                                        <Route path="/rca/5why/:incidentId" element={<FiveWhyTool />} />
                                        <Route path="/rca/ishikawa" element={<IshikawaTool />} />
                                        <Route path="/rca/ishikawa/:incidentId" element={<IshikawaTool />} />
                                        <Route path="/rca/templates" element={<MethodologyTemplates />} />
                                        <Route path="/analytics" element={<AnalyticsDashboard />} />
                                        <Route path="/analytics/hub" element={<AdvancedAnalyticsHub />} />
                                        <Route path="/analytics/weibull" element={<WeibullTool />} />
                                        <Route path="/analytics/mtbf" element={<MTBFCalculator />} />
                                        <Route path="/analytics/warranty" element={<WarrantyTracking />} />
                                        <Route path="/analytics/fta" element={<FTAEditor />} />
                                        <Route path="/analytics/rbd" element={<RBDTool />} />
                                        <Route path="/executive" element={<ExecutiveCockpit />} />
                                        <Route path="/reports/advanced" element={<AdvancedReports />} />
                                        <Route path="/reports/generator" element={<AdvancedReports />} />
                                        <Route path="/lab" element={<LabSampleList />} />
                                        <Route path="/lab/:id" element={<LabSampleDetail />} />
                                        <Route path="/ai-assistant" element={<AIAssistantPage />} />
                                        <Route path="/user-guide" element={<UserGuidePage />} />
                                    </Route>

                                    {/* Catch all - redirect to dashboard */}
                                    <Route path="*" element={<Navigate to="/" replace />} />
                                </Routes>
                            </LabSampleProvider>
                        </EightDProvider>
                    </FracasProvider>
                </LearnProvider>
            </TourProvider>
        </ThemeProvider>
    );
};

export default App;
